import { useEffect, useRef } from "react";
import { Link, useParams } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "@/lib/trpc";
import ClarityOneLayout from "@/components/clarity-one/ClarityOneLayout";
import {
  completeSuccessfulImpersonationHandoff,
  markImpersonationVerifyStarted,
  shouldStartImpersonationVerify,
} from "@/lib/clarityOneImpersonationHandoff";
import { Loader2, Link2Off } from "lucide-react";

/** Consumes a one-time staff impersonation token and opens Clarity One as the customer. */
export default function ClarityOneAuthImpersonatePage() {
  const params = useParams<{ token: string }>();
  const token = params.token ?? "";
  const queryClient = useQueryClient();
  const startedRef = useRef(false);
  const handedOffRef = useRef(false);

  const verify = trpc.clarityOneAuth.verifyImpersonationToken.useMutation({
    onSuccess: () => {
      if (handedOffRef.current) return;
      handedOffRef.current = true;
      completeSuccessfulImpersonationHandoff(queryClient);
    },
  });

  useEffect(() => {
    if (
      !shouldStartImpersonationVerify({
        token,
        alreadyStarted: startedRef.current,
        isPending: verify.isPending,
        isSuccess: verify.isSuccess,
        isError: verify.isError,
      })
    ) {
      return;
    }
    startedRef.current = true;
    if (!markImpersonationVerifyStarted(token)) return;
    verify.mutate({ token });
  }, [token, verify.isPending, verify.isSuccess, verify.isError, verify.mutate]);

  return (
    <ClarityOneLayout showNav={false}>
      <div className="flex flex-col items-center justify-center py-20 text-center px-4">
        {verify.isError ? (
          <>
            <div className="h-14 w-14 rounded-full bg-[#FAFAF7] border border-[#E8E4DC] flex items-center justify-center mb-5">
              <Link2Off className="h-6 w-6 text-[#8B7355]" />
            </div>
            <p className="text-lg font-medium text-[#1A2744] mb-2">Unable to open customer view</p>
            <p className="text-sm text-[#8B7355] leading-relaxed max-w-sm mb-8">
              {verify.error.message || "This impersonation link is invalid or has expired."}
            </p>
            <Link
              href="/login"
              className="inline-flex items-center justify-center rounded-xl bg-[#1A2744] hover:bg-[#243556] text-white px-6 py-2.5 text-sm font-medium no-underline"
            >
              Go to Clarity One login
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-[#C8A96A] mb-4" />
            <p className="text-sm text-[#8B7355]">Opening Clarity One as customer…</p>
          </>
        )}
      </div>
    </ClarityOneLayout>
  );
}
